import React, { RefObject } from "react";
import { ProposalComment } from "src/redux/projects";
import { commentIdDnd, extractCommentIdDnd } from "src/helpers/dragndrop";
import ReactPanZoom from "@ajainarayanan/react-pan-zoom";
import { Tooltip } from "react-tippy";
import { config } from '../config/config';

interface Props {
  /**
   * Image source
   */
  url: string;

  /**
   * Css class to apply to img tag
   */
  className?: string;

  /**
   * Transmit drop on image
   */
  onDrop?: (
    x: number,
    y: number,
    commentId: string,
    commentNumber: number
  ) => void;

  /**
   * Transmit click on a pin
   */
  onPinclick?: (commentId: string, commentNumber: number) => void;

  /**
   * Comments to display on image
   */
  comments: ProposalComment[];

  /**
   * Zoom of image with pin
   */
  zoom: number;

  /**
   * Prevent all drag events
   */
  locked?: boolean;

  /**
   * Triggered when image has loaded
   */
  onImageLoad?: (image: HTMLImageElement) => void;
}

interface State {
  // We need to know when image has loaded, so we can trigger a new render to draw pin
  imageLoaded: boolean;
}

/**
 * An image with pin for comments
 * Can be zoomed and panned
 */
export class ImagePanWithPin extends React.Component<Props, State> {
  imageRef: RefObject<HTMLImageElement> = React.createRef();
  state: State = {
    imageLoaded: false
  };
  /**
   * Trigger a new render once image has loaded, so pin are drawed immediatly after
   */
  onImageLoad = () => {
    if (!this.imageRef || !this.imageRef.current) {
      return;
    }

    if (this.props.onImageLoad) {
      this.props.onImageLoad(this.imageRef.current);
    }

    this.setState({ imageLoaded: true });
  };

  /**
   * We do nothing special here
   * But we need to intercep drag over event to make the onDrop event work
   */
  onDragOver = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    e.nativeEvent.stopImmediatePropagation();
  };

  /**
   * Return x and y coords to place a pin in image
   */
  calculatePosition = e => {
    if (!this.imageRef || !this.imageRef.current) {
      return;
    }

    /**
     * Pin are registered using their coordinates using the original image as reference
     * Then pin are displayed on a responsive image, which is smaller than original image
     * we calculate xFactor & yFactor to transform pin's real coordinates in display coordinates
     */
    const xFactor =
      this.imageRef.current.naturalWidth / this.imageRef.current.width;
    const yFactor =
      this.imageRef.current.naturalHeight / this.imageRef.current.height;

    const x = e.nativeEvent.offsetX * xFactor;
    const y = e.nativeEvent.offsetY * yFactor;

    // We force coordinates between image max dimensions
    const xFloor = Math.max(0, Math.min(this.imageRef.current.naturalWidth, x));
    const yFloor = Math.max(
      0,
      Math.min(this.imageRef.current.naturalHeight, y)
    );
    return { xFloor, yFloor };
  };
  /**
   * When a pin is dropped here, we calculate it's new coordinate and inform parent component
   */
  onDrop = e => {
    if (!this.props.onDrop) {
      return;
    }

    const data = e.dataTransfer.getData("text");
    const commentId = extractCommentIdDnd(data);

    if (!commentId) {
      return;
    }

    const position = this.calculatePosition(e);
    if (!position) {
      return;
    }
    this.props.onDrop(
      position.xFloor,
      position.yFloor,
      commentId,
      this.props.comments.length
    );
  };
  /**
   * Create a new pin
   */
  createNewComment = ev => {
    ev.stopPropagation();

    if (!this.props.onDrop) {
      return;
    }
    const position = this.calculatePosition(ev);
    if (!position) {
      return;
    }
    this.props.onDrop(
      position.xFloor,
      position.yFloor,
      "0",
      this.props.comments.length
    );
  };
  /**
   * When a pin start's to move, we store it's comment id in dataTransfer, so other components will now who is moving
   */
  handlePinDragStart = (
    ev: React.DragEvent<HTMLDivElement>,
    comment: ProposalComment
  ) => {
    ev.dataTransfer.setData("text/plain", commentIdDnd(comment.id));
  };

  /**
   * Render a Pin, considering it's comment coordinates
   */
  renderPin = (comment: ProposalComment, index) => {
    if (!this.imageRef || !this.imageRef.current || !this.state.imageLoaded) {
      return;
    }

    // Correct left position considering SVG icon. Icon size defined in css stylesheet
    // It's in % of current image size
    const pinLeftOffset = (20 / this.imageRef.current.width) * 100;
    const pinTopOffset = (20 / this.imageRef.current.height) * 100;

    // Pin are positionned in %, so it can be responsive
    const left =
      (comment.x / this.imageRef.current.naturalWidth) * 100 - pinLeftOffset;
    const top =
      (comment.y / this.imageRef.current.naturalHeight) * 100 - pinTopOffset;

    return (
      <Tooltip
        html={comment.text}
        position="bottom"
        trigger="mouseenter"
        key={comment.id}
        className="pin"
        style={{
          top: top + "%",
          left: left + "%"
        }}
      >
        <div
          onClick={() =>
            this.props.onPinclick &&
            this.props.onPinclick(comment.id, this.props.comments.length)
          }
          draggable={true}
          onDragStart={e => this.handlePinDragStart(e, comment)}
          onMouseDown={e => e.stopPropagation()}
          className={this.props.comments.length > config.maxCommentCount-5 ? "red" : "orange"}
        >
          {index + 1}
        </div>
      </Tooltip>
    );
    return;
  };
  render() {
    return (
      <ReactPanZoom className="pan-div" zoom={this.props.zoom}>
        <div className="images-comments">
          <div
            className="comments-display"
            onDragOver={this.onDragOver}
            onDrop={this.onDrop}
          >
            {this.props.comments.map((c, index) => this.renderPin(c, index))}
          </div>
          <img
            src={this.props.url}
            className={this.props.className}
            ref={this.imageRef}
            onLoad={this.onImageLoad}
          />
        </div>
      </ReactPanZoom>
    );
  }
}



// WEBPACK FOOTER //
// ./src/components/ImagePanWithPin.tsx